import { SelectOption } from "../types/select.types";

export const findSelectOption = <T extends SelectOption>(
  key: T["name"] | T["value"] | "",
  options: readonly T[]
): T | undefined => options.find((option) => option.name === key || option.value === key);

export const toSelectValue = <T extends SelectOption>(name: T["name"] | "" | undefined, options: readonly T[]): T[] => {
  if (!name) {
    return [];
  }

  const option = findSelectOption(name, options);

  return option ? [option] : [];
};

export const mapNamesToSelectOptions = (value: unknown, options: readonly SelectOption[]): SelectOption[] => {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => {
      if (typeof entry === "string") {
        return findSelectOption(entry, options);
      }

      if (entry && typeof entry === "object" && "name" in entry && typeof entry.name === "string") {
        return findSelectOption(entry.name, options);
      }

      if (entry && typeof entry === "object" && "value" in entry && typeof entry.value === "string") {
        return findSelectOption(entry.value, options);
      }

      return undefined;
    })
    .filter((option): option is SelectOption => Boolean(option));
};

// Adds an option to the list, or removes it if it's already selected (toggle behavior)
export const toggleMultipleSelection =
  (option: SelectOption) =>
  (currentSelection: SelectOption[]): SelectOption[] => {
    if (currentSelection.some(matchesOption(option))) {
      return currentSelection.filter(
        (item) => (item?.value || item) !== option.value && (item?.name || item) !== option.name
      );
    }
    return [...currentSelection, option];
  };

// Returns true if the given item matches the target option
export const matchesOption =
  (option: SelectOption) =>
  (item: SelectOption): boolean => {
    if (!item) return false;
    return option.value === (item.value || item) && option.name === (item.name || item);
  };
